import { takeEvery, all, call, put, fork, select } from 'redux-saga/effects'
import { snakeCase } from 'snake-case'

import { NAV_BRAND, APPLICATION, RATING_WIDGET, SEARCH } from '@/config'
import { captureExtendedException } from '@/utils/assert'
import { captureException } from '@/utils/asyncSentry'

import { PORTAL_TYPE_YF } from '../../config/constants'
import { JOB_STATUS_INVISIBLE } from '../../jobDetail/constants'
import { getJobInfo } from '../../jobDetail/store/selectors'
import { JOB_THEMES } from '../../page/constants'
import { getPageType } from '../../page/store/selectors'
import { renderRatingWidget } from '../../ratingWidget/store/actions'
import { getEnabled as getRatingWidgetEnabled } from '../../ratingWidget/store/selectors'
import { JOB, OPENING_TYPE } from '../../search/constants'
import { getAutoSelectItem, getSearchType, getSelectedId } from '../../search/store/selectors'
import { api } from '../../services'
import { getSimilarJobsParams } from '../../similarJobs/store/selectors'
import { isKalaydo, isSta } from '../../tracking/utils/detatched'
import {
  prepareCompanyInfo,
  prepareJobInfo,
  trackApplicationSend,
  trackCompanyJobApplicationSend,
  trackExpressApplicationSend,
  trackJobForApplicationRequest,
  trackJobForApplicationSuccess,
  trackReuseAttachmentsUsage,
  trackReuseAttachmentsView,
  trackReusedAttachmentsTicked
} from '../../tracking/utils/tracking'
import { appendQueryParams } from '../../utils/url'
import mapKeys from '../lib/map-keys'

import {
  submit,
  submitSuccess,
  submitFailure,
  showJobApplication,
  loadAttachmentsSuccess,
  setDraftAttachmentsToReuse,
  sendDraftAttachmentsToReuse,
  loadJobForApplicationError,
  loadJobForApplicationSuccess,
  loadJobForApplication
} from './actions'
import {
  reusingAttachmentsAllowed,
  getAttachmentsToReuseFromDraft,
  getAttachmentsToReuse,
  getExcludeIds,
  getJobForApplication,
  getLoopValue
} from './selectors'

const GENERIC_ERROR_MESSAGE = 'Es ist ein unbekannter Fehler aufgetreten.'

/**
 * @private exposed only for tests
 */
export function * uploadFile (file) {
  if (file.key) {
    // it's not needed to upload a file which has been already uploaded and now it's being reused
    return file.key
  }
  const { data: signedResponse } = yield call(api.signS3Upload, file)
  yield call(api.uploadS3, file, signedResponse.data)
  const { data: { fields: { key } } } = signedResponse
  return key
}

export function * loadJobInfoForApplication () {
  try {
    const excludeIds = yield select(getExcludeIds)
    if (excludeIds.length >= APPLICATION.maxJobsForApplication) {
      yield put(loadJobForApplicationSuccess(null))
      return
    }
    const selectedId = yield select(getSelectedId)
    const prevJobForApplication = yield select(getJobForApplication)
    const jobId = prevJobForApplication?.id || selectedId
    const { forUkraine } = SEARCH
    const similarJobsParams = yield select(getSimilarJobsParams)
    const params = {
      with_backfill: similarJobsParams.with_backfill || 0,
      ...mapKeys({
        forUkraine: Number(forUkraine)
      }, snakeCase)
    }
    if (excludeIds.length) {
      params.exclude_ids = excludeIds.join(',')
    }
    trackJobForApplicationRequest(excludeIds.length + 1)
    const response = yield call(api.requestJobForApplication, jobId, params)
    const { data: responseData, status } = response
    if (status >= 500) {
      yield put(loadJobForApplicationError(responseData))
      return
    }
    const jobForApplication = status === 200 ? responseData?.data : null
    trackJobForApplicationSuccess(excludeIds.length + 1, !!jobForApplication?.id)
    yield put(loadJobForApplicationSuccess({ appliedJobId: jobId, jobForApplication }))
  } catch (e) {
    yield call(captureExtendedException, e)
    yield put(loadJobForApplicationError((e.response ? e.response.data : null) || { message: GENERIC_ERROR_MESSAGE }))
  }
}

/**
 * @private exposed only for tests
 */
export function * submitJobApplication ({ payload: { formData: { documents = [], ...form }, formType, url }, meta: { info } }) {
  try {
    const fileKeys = yield all(documents.map((doc) => call(uploadFile, doc)))
    const searchType = yield select(getSearchType)
    const isJob = searchType === JOB
    const jobForApplication = yield select(getJobForApplication)
    const loop = yield select(getLoopValue)
    const autoSelectItem = yield select(getAutoSelectItem)
    const isNimm2Feature = jobForApplication?.id === info.id
    const trackingInfo = yield call(isJob ? prepareJobInfo : prepareCompanyInfo, info, {
      [`${isJob ? 'job' : 'profile'}Opening`]: autoSelectItem ? OPENING_TYPE.AUTO : OPENING_TYPE.CLICK,
      trackKununuListRating: NAV_BRAND.portal_type === PORTAL_TYPE_YF,
      isNimm2Feature,
      loop: isNimm2Feature ? loop : null
    })

    let apiCall, trackingMethod
    switch (formType) {
      case 'detail':
        apiCall = api.submitDetailedJobApplication
        if (isJob) {
          trackingMethod = trackApplicationSend
        } else {
          trackingMethod = trackCompanyJobApplicationSend
        }
        break
      case 'express':
        trackingMethod = isJob
          ? trackExpressApplicationSend
          : () => { }
        apiCall = api.submitExpressJobApplication
        break
      default:
        throw new Error(`Unsupported job application form type ${formType}`)
    }

    if (!isJob || info.status !== JOB_STATUS_INVISIBLE) {
      yield call(trackingMethod, trackingInfo)
    }

    const formData = { ...mapKeys(form, snakeCase), 'file_keys[]': fileKeys }

    if (isSta()) {
      url = appendQueryParams(url, { from_sta: '1' })
    }
    if (isKalaydo()) {
      url = appendQueryParams(url, { from_kalaydo: '1' })
    }

    const { data } = yield call(apiCall, url, formData)

    if (formType === 'detail') {
      const reusingIsAllowed = yield select(reusingAttachmentsAllowed)
      yield call(handleAttachments, fileKeys, reusingIsAllowed)

      if (reusingIsAllowed) {
        const attachments = yield select(getAttachmentsToReuse)
        const areAttachmentsReused = attachments.some(attachment => fileKeys.includes(attachment.key))
        yield call(trackReuseAttachmentsUsage, areAttachmentsReused)
      }

      if (isJob && isNimm2Feature) {
        yield call(loadJobInfoForApplication)
      }
    }

    yield put(submitSuccess(data))
    // show rating widget if it'as visible
    const widgetEnabled = yield select(getRatingWidgetEnabled)
    const pageType = yield select(getPageType)
    if (RATING_WIDGET.enabled && widgetEnabled && !JOB_THEMES.includes(pageType)) {
      yield put(renderRatingWidget())
    }
  } catch (e) {
    yield call(captureExtendedException, e)
    yield put(submitFailure((e.response ? e.response.data : null) || { message: GENERIC_ERROR_MESSAGE }))
  }
}

export function * getAttachments () {
  try {
    const { data } = yield call(api.getAttachments)
    yield put(loadAttachmentsSuccess(data))
    const reusingIsAllowed = yield select(reusingAttachmentsAllowed)
    const info = yield select(getJobInfo)
    if (!info || info.status !== JOB_STATUS_INVISIBLE) {
      yield call(trackReuseAttachmentsView, reusingIsAllowed)
    }
  } catch (e) {
    yield call(captureException, e)
    return {}
  }
}

export function * handleAttachments (fileKeys, reusingIsAllowed) {
  if (fileKeys?.length > 0) {
    if (reusingIsAllowed) {
      yield call(api.postAttachmentsInfo, { 'file_keys[]': fileKeys })
    } else {
      // save the attached files into the state. If a user allows to reuse them, they will be sent to the backend
      yield put(setDraftAttachmentsToReuse({ draft: fileKeys }))
    }
  }
}

export function * sendAttachmentsFromDraft () {
  const fileKeys = yield select(getAttachmentsToReuseFromDraft)
  yield call(handleAttachments, fileKeys, true)
  const info = yield select(getJobInfo)
  if (!info || info.status !== JOB_STATUS_INVISIBLE) {
    yield call(trackReusedAttachmentsTicked)
  }
}

export function * watchViewSubmit () {
  yield all([
    takeEvery(submit, submitJobApplication),
    takeEvery(showJobApplication, getAttachments),
    takeEvery(sendDraftAttachmentsToReuse, sendAttachmentsFromDraft),
    takeEvery(loadJobForApplication, loadJobInfoForApplication)
  ])
}

export default fork(watchViewSubmit)
